import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  UsersIcon,
  EyeIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FireIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const RealTimeAnalytics = () => {
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realTimeData, setRealTimeData] = useState({
    activeUsers: 0,
    pageViews: 0,
    events: 0,
    conversions: 0,
    revenue: 0,
    topPages: [],
    topEvents: [],
    usersByCountry: [],
    deviceTypes: [],
    trafficSources: [],
    recentActivity: [],
    performanceMetrics: {},
    alertsAndInsights: []
  });
  const [timeRange, setTimeRange] = useState('1h'); // 1h, 6h, 24h
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds

  // Fetch real-time data
  const fetchRealTimeData = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`/api/admin/analytics/real-time?range=${timeRange}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        setRealTimeData(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'שגיאה בטעינת נתונים בזמן אמת');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Auto-refresh effect
  useEffect(() => {
    fetchRealTimeData();

    if (autoRefresh) {
      const interval = setInterval(fetchRealTimeData, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [fetchRealTimeData, autoRefresh, refreshInterval]);

  // Track page view
  useEffect(() => {
    trackEvent('page_view', {
      page_title: 'Real-time Analytics Dashboard',
      page_path: '/admin/real-time-analytics'
    });
  }, [trackEvent]);

  // Format numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount || 0);
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend > 0) return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
    if (trend < 0) return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    return <ClockIcon className="h-4 w-4 text-gray-400" />;
  };

  // Colors for charts
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="mr-3 text-gray-600">טוען נתונים בזמן אמת...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={fetchRealTimeData}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          נסה שוב
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg" dir="rtl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
            <BoltIcon className="h-8 w-8 ml-2 text-yellow-500" />
            אנליטיקה בזמן אמת
          </h1>
          <p className="text-gray-600">
            מעקב חי אחר פעילות המשתמשים באתר
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-4 space-x-reverse">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="1h">שעה אחרונה</option>
            <option value="6h">6 שעות אחרונות</option>
            <option value="24h">24 שעות אחרונות</option>
          </select>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="autoRefresh" className="text-sm text-gray-700">
              רענון אוטומטי
            </label>
          </div>

          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
            disabled={!autoRefresh}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="10">כל 10 שניות</option>
            <option value="30">כל 30 שניות</option>
            <option value="60">כל דקה</option>
          </select>

          <button
            onClick={fetchRealTimeData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            רענן עכשיו
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">משתמשים פעילים</p>
              <p className="text-3xl font-bold">{formatNumber(realTimeData.activeUsers)}</p>
            </div>
            <UsersIcon className="h-12 w-12 text-blue-200" />
          </div>
          <div className="flex items-center mt-2">
            {getTrendIcon(realTimeData.performanceMetrics?.activeUsersTrend)}
            <span className="text-blue-100 text-sm mr-1">
              {Math.abs(realTimeData.performanceMetrics?.activeUsersTrend || 0)}%
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">צפיות בדף</p>
              <p className="text-3xl font-bold">{formatNumber(realTimeData.pageViews)}</p>
            </div>
            <EyeIcon className="h-12 w-12 text-green-200" />
          </div>
          <div className="flex items-center mt-2">
            {getTrendIcon(realTimeData.performanceMetrics?.pageViewsTrend)}
            <span className="text-green-100 text-sm mr-1">
              {Math.abs(realTimeData.performanceMetrics?.pageViewsTrend || 0)}%
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">אירועים</p>
              <p className="text-3xl font-bold">{formatNumber(realTimeData.events)}</p>
            </div>
            <ChartBarIcon className="h-12 w-12 text-yellow-200" />
          </div>
          <div className="flex items-center mt-2">
            {getTrendIcon(realTimeData.performanceMetrics?.eventsTrend)}
            <span className="text-yellow-100 text-sm mr-1">
              {Math.abs(realTimeData.performanceMetrics?.eventsTrend || 0)}%
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">המרות</p>
              <p className="text-3xl font-bold">{formatNumber(realTimeData.conversions)}</p>
            </div>
            <ShoppingCartIcon className="h-12 w-12 text-purple-200" />
          </div>
          <div className="flex items-center mt-2">
            {getTrendIcon(realTimeData.performanceMetrics?.conversionsTrend)}
            <span className="text-purple-100 text-sm mr-1">
              {Math.abs(realTimeData.performanceMetrics?.conversionsTrend || 0)}%
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">הכנסות</p>
              <p className="text-2xl font-bold">{formatCurrency(realTimeData.revenue)}</p>
            </div>
            <CurrencyDollarIcon className="h-12 w-12 text-red-200" />
          </div>
          <div className="flex items-center mt-2">
            {getTrendIcon(realTimeData.performanceMetrics?.revenueTrend)}
            <span className="text-red-100 text-sm mr-1">
              {Math.abs(realTimeData.performanceMetrics?.revenueTrend || 0)}%
            </span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Pages */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <EyeIcon className="h-5 w-5 ml-2 text-blue-500" />
            דפים פופולריים
          </h3>
          <div className="space-y-3">
            {realTimeData.topPages?.slice(0, 5).map((page, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{page.title}</p>
                  <p className="text-xs text-gray-500 truncate">{page.path}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-blue-600">{formatNumber(page.views)}</p>
                  <p className="text-xs text-gray-500">{page.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Device Types */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <DevicePhoneMobileIcon className="h-5 w-5 ml-2 text-green-500" />
            סוגי מכשירים
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={realTimeData.deviceTypes}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, percentage }) => `${name} ${percentage}%`}
              >
                {realTimeData.deviceTypes?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <GlobeAltIcon className="h-5 w-5 ml-2 text-purple-500" />
            מקורות תנועה
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={realTimeData.trafficSources}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="source" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="sessions" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Events */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FireIcon className="h-5 w-5 ml-2 text-red-500" />
            אירועים פופולריים
          </h3>
          <div className="space-y-3">
            {realTimeData.topEvents?.slice(0, 5).map((event, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{event.name}</p>
                  <p className="text-xs text-gray-500">{event.category}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-red-600">{formatNumber(event.count)}</p>
                  <p className="text-xs text-gray-500">{event.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <ClockIcon className="h-5 w-5 ml-2 text-gray-500" />
          פעילות אחרונה
        </h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {realTimeData.recentActivity?.map((activity, index) => (
            <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full ml-3"></div>
                <div>
                  <p className="text-sm text-gray-800">{activity.event}</p>
                  <p className="text-xs text-gray-500">{activity.page}</p>
                </div>
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500">{activity.time}</p>
                <p className="text-xs text-gray-400">{activity.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Alerts and Insights */}
      {realTimeData.alertsAndInsights?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
            <BoltIcon className="h-5 w-5 ml-2 text-yellow-600" />
            התראות ותובנות
          </h3>
          <div className="space-y-3">
            {realTimeData.alertsAndInsights.map((alert, index) => (
              <div key={index} className={`p-3 rounded-md ${
                alert.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                alert.type === 'success' ? 'bg-green-100 text-green-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                <p className="text-sm font-medium">{alert.title}</p>
                <p className="text-xs mt-1">{alert.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeAnalytics;
